import { type JSX, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  empfaengerKontaktSetzen,
  kontaktauswahl,
  type Rechnung,
  type Rechnungskontakt,
} from '../api/rechnungen';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/**
 * KUN-08, BUC-103: den Rechnungsempfänger am Entwurf wechseln.
 *
 * Beim Anlegen steht der markierte Ansprechpartner schon an der Rechnung.
 * Hier lässt er sich vor dem Festschreiben gegen einen anderen tauschen oder
 * ganz abnehmen — dann geht die Rechnung an das Unternehmen, ohne „z. Hd.“.
 *
 * Keine Rückfrage: Der Entwurf bleibt änderbar, die Wahl ist umkehrbar
 * (Tabelle 29, ÄA-62 B). Einspaltig, auch auf dem Telefon (ÄA-63).
 */
export function Empfaengerwahl({
  rechnung,
  beiGewechselt,
  beiSchliessen,
}: {
  readonly rechnung: Rechnung;
  readonly beiGewechselt: (meldung: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [kontakte, setzeKontakte] = useState<readonly Rechnungskontakt[] | undefined>(undefined);
  const [wahl, setzeWahl] = useState<string | null>(rechnung.empfaengerKontaktrolle);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  useEffect(() => {
    let aktuell = true;
    kontaktauswahl(rechnung.id).then(
      (liste) => {
        if (aktuell) setzeKontakte(liste);
      },
      (ursache: unknown) => {
        if (aktuell) setzeFehler(ursache as ApiFehler);
      },
    );
    return () => {
      aktuell = false;
    };
  }, [rechnung.id]);

  const uebernehmen = async (): Promise<void> => {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      await empfaengerKontaktSetzen(rechnung.id, wahl, rechnung.stand);
      const name = kontakte?.find((k) => k.kontaktrolle === wahl)?.name;
      beiGewechselt(
        name === undefined
          ? 'Die Rechnung geht an das Unternehmen, ohne „z. Hd.“.'
          : `Die Rechnung geht jetzt z. Hd. ${name}.`,
      );
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  };

  const schliessen = (): void => {
    if (!laeuft) beiSchliessen();
  };

  return (
    <Dialog
      titel="Rechnungsempfänger wechseln"
      unterzeile="An wen die Rechnung geht — „z. Hd.“ in der Anschrift und Empfänger der E-Mail."
      breite="schmal"
      beiSchliessen={schliessen}
    >
      <>
        {kontakte === undefined && fehler === undefined && <p role="status">Wird geladen …</p>}
        {kontakte !== undefined && (
          <fieldset className="feld">
            <legend className="feld__beschriftung">Ansprechpartner</legend>
            <label className="liste__schalter">
              <input type="radio" name="empfaenger" checked={wahl === null} onChange={() => setzeWahl(null)} />
              an das Unternehmen, ohne „z. Hd.“
              {rechnung.empfaengerEmail !== null && ` (${rechnung.empfaengerEmail})`}
            </label>
            {kontakte.map((k) => (
              <label key={k.kontaktrolle} className="liste__schalter">
                <input
                  type="radio"
                  name="empfaenger"
                  checked={wahl === k.kontaktrolle}
                  onChange={() => setzeWahl(k.kontaktrolle)}
                />
                {k.name}
                {k.email !== null ? ` (${k.email})` : ' (ohne E-Mail-Adresse)'}
                {k.rechnungsempfaenger && ' · als Rechnungsempfänger markiert'}
              </label>
            ))}
            {kontakte.length === 0 && (
              <p className="feld__hilfe">Der Kunde führt keine Ansprechpartner.</p>
            )}
            <p className="feld__hilfe">
              Ohne E-Mail-Adresse des Ansprechpartners geht die E-Mail an die Adresse des
              Unternehmens; „z. Hd.“ steht trotzdem in der Anschrift.
            </p>
          </fieldset>
        )}
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <Abschlussreihe
          abbruch={
            <button type="button" className="knopf" disabled={laeuft} onClick={schliessen}>
              Abbrechen
            </button>
          }
        >
          <button
            type="button"
            className="knopf knopf--haupt"
            disabled={laeuft || kontakte === undefined || wahl === rechnung.empfaengerKontaktrolle}
            aria-busy={laeuft}
            onClick={() => void uebernehmen()}
          >
            Übernehmen
          </button>
        </Abschlussreihe>
      </>
    </Dialog>
  );
}
